<template>
  <div class="sidebar-container">
    <div class="control-options">
      <search
        v-if="!viewingFilters"
        v-model="searchText"
        v-on:input="onSearchChange"
      ></search>
      <rounded-button v-else :onClick="applyFilterChanges"
        >Apply Filter Updates</rounded-button
      >
      <filter-button v-if="!viewingFilters" :onClick="toggleFilterView" />
      <x-button v-if="viewingFilters" :onClick="toggleFilterView" />
      <add-location-button :onClick="addLocationPressed" />
    </div>
    <loading :working="loading"></loading>
    <div class="sidebar-content-container" v-if="!loading">
      <div v-if="viewingFilters">
        <filter-settings :settings="filterSettings"></filter-settings>
      </div>
      <div v-else-if="selectedMappable">
        <selected-site-details
          :selected="selectedMappable"
        ></selected-site-details>
      </div>
      <div v-else-if="searching">
        <search-results
          :filteredMappables="visibleMappables"
          :onClick="onClickMappable"
        >
        </search-results>
      </div>
      <div v-else>
        <mappables-list
          :mappables="visibleMappables"
          :onClick="onClickMappable"
        >
        </mappables-list>
      </div>
    </div>
  </div>
</template>

<script>
import MappablesList from './sidebar/mappables-list';
import Search from './sidebar/search';
import SelectedSiteDetails from './sidebar/selected-site-details';
import SearchResults from './sidebar/search-results';
import FilterSettings from './sidebar/filter-settings';
import RoundedButton from '../common/rounded-button';
import { mapGetters } from 'vuex';
import FilterButton from './sidebar/filter-button';
import XButton from './sidebar/x-button';
import AddLocationButton from './sidebar/add-location-button';
import SiteService from '../../services/v2/SiteService';
import NoSpraysService from '../../services/v2/NoSpraysService';
import NoSpray from '../../store/models/no-spray';
import ServiceRequest from '../../store/models/service-request';
import ServiceRequestsService from '../../services/v2/ServiceRequestsService';
import getProperCoordinateType from '../../utils/getProperCoordinateType';
import Site from '../../store/models/site';
import loading from '@/components/loading';

export default {
  components: {
    FilterSettings,
    MappablesList,
    RoundedButton,
    Search,
    SelectedSiteDetails,
    SearchResults,
    FilterButton,
    XButton,
    AddLocationButton,
    loading,
  },

  props: {
    viewingFilters: Boolean,
  },

  data: () => ({
    searchTimeout: null,
  }),

  computed: {
    ...mapGetters(['visibleMappables']),
    selectedMappable() {
      const { selectedMappable } = this.$store.state.map;
      return selectedMappable;
    },
    preparingSearch() {
      return this.$store.state.map.executeSearch;
    },
    searching() {
      return !!this.searchText;
    },
    noMappableSelected() {
      const { selectedMappable } = this.$store.state.map;
      return !selectedMappable;
    },
    filterSettings() {
      const {
        displayFilterSettings,
        filters: filtersSettings,
      } = this.$store.state.map.settings;
      if (!displayFilterSettings) {
        return null;
      }
      const filtersSettingsKeys = Object.keys(filtersSettings);
      const reducer = (acc, key) => ({
        ...acc,
        [key]: {
          ...filtersSettings[key],
        },
      });
      const filtersSettingsCopy = filtersSettingsKeys.reduce(reducer, {});
      return filtersSettingsCopy;
    },
    loading() {
      const { loadingMoreMappables } = this.$store.state.map;
      return loadingMoreMappables;
    },
  },

  data() {
    return {
      searchText: '',
    };
  },

  methods: {
    onSearchChange(search) {
      const { $store } = this;
      const { dispatch } = $store;
      clearTimeout(this.searchTimeout);
      if (!search) {
          dispatch('setMappableSearch', { search });
          $store.commit('setLoadingMoreMappables', false);
          return;
      }
      
      $store.commit('setLoadingMoreMappables', true);
      this.searchTimeout = setTimeout(() => {
        SiteService.getSitesByName(search)
        .then(sitesRes => {
          NoSpraysService.getNoSpraysByName(search)
          .then(noSpraysRes => {
            ServiceRequestsService.getServiceRequestsByName(search)
            .then(serviceRequestsRes => {
              const sites = sitesRes.data.sites;
              const noSprays = noSpraysRes.data.noSprays;
              const serviceRequests = serviceRequestsRes.data.serviceRequests;
              const outstandingServiceRequests = serviceRequests.filter(
                ({ status }) => status === 'Outstanding'
              );

              var newMappables = [];
              newMappables.push.apply(
                newMappables,
                sites.map((site) => new Site(site))
              );
              newMappables.push.apply(
                newMappables,
                noSprays.map((noSpray) => new NoSpray(noSpray))
              );
              newMappables.push.apply(
                newMappables,
                outstandingServiceRequests.map(
                  (outstandingServiceRequest) =>
                    new ServiceRequest(outstandingServiceRequest)
                )
              );


              newMappables = newMappables.filter((mappable) => {
                const properCoordinates = getProperCoordinateType(mappable);
                if (properCoordinates === false) {
                  return false;
                }
                mappable.coordinates = properCoordinates;
                return true;
              });

              $store.commit('updateSites', newMappables);

              $store.commit('handleChangeFilters', {
                ...$store.state.map.settings.filters,
              });

              dispatch('setMappableSearch', { search });
              $store.commit('setLoadingMoreMappables', false);
            })
          })
        })
      }, 340);
    },
    onClickMappable(mappable) {
      const { dispatch } = this.$store;
      dispatch('selectMappable', mappable);
    },
    toggleFilterView() {
      this.$store.dispatch('setDisplayFilterSettings', !this.viewingFilters);
    },
    applyFilterChanges() {
      const { $store, filterSettings } = this;
      const { dispatch } = $store;
      this.toggleFilterView();
      dispatch('applyFilterSettings', filterSettings);
    },
    addLocationPressed() {
      this.$router.push({
        name: 'data-sites-new',
        params: {
          id: 'new',
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables';

.sidebar-container {
  overflow: hidden;
  padding: 0 0 0 2rem;
  width: 30.5rem;
  height: 800px;
  line-height: initial;

  & .control-options {
    display: flex;
    flex-direction: row;
    margin-bottom: 1rem;
  }

  & .sidebar-content-container {
    overflow: hidden;
    height: 762px;

    & div {
      height: 100%;
    }
  }
}
</style>
